import { useState, useCallback, useEffect } from 'react';
import { firebaseDatabaseService } from '../database/firebaseDatabase.js';

export function useTasks(user, selectedDate = null, options = {}) {
  const { onAddTask } = options || {};
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recurringUpdateDialog, setRecurringUpdateDialog] = useState({
    open: false,
    taskId: null,
    updates: null,
  });

  // State for recurring task delete dialog
  const [recurringDeleteDialog, setRecurringDeleteDialog] = useState({
    open: false,
    taskId: null,
  });

  // Auto-extend recurring tasks periodically (check weekly)
  const extendRecurringTasks = useCallback(async () => {
    if (!user) return;

    try {
      const allTasks = await firebaseDatabaseService.getAllTasks(user.uid);
      const today = new Date();

      // Create a set of existing dates to avoid duplicates
      const existingDates = new Set(allTasks.map(task => task.dateString));

      // Find recurring tasks that need to be extended
      const recurringTasks = allTasks.filter(task =>
        task.repeat && task.repeat !== 'none' && task.dateString
      );

      for (const task of recurringTasks) {
        const lastDate = new Date(task.dateString);
        const daysDiff = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));

        // Handle both string and object repeat patterns
        let repeatPattern = task.repeat;
        if (typeof task.repeat === 'object' && task.repeat.frequency) {
          repeatPattern = task.repeat.frequency;
        }

        // Only extend if the task is more than 1 day old
        if (daysDiff > 0) {
          switch (repeatPattern) {
            case 'daily':
              for (let i = 1; i <= daysDiff; i++) {
                const newDate = new Date(lastDate);
                newDate.setDate(newDate.getDate() + i);
                const newDateString = newDate.toISOString().split('T')[0];

                if (existingDates.has(newDateString)) continue;

                if (newDate <= today) {
                  const newTaskData = {
                    ...task,
                    date: newDate,
                    dateString: newDateString,
                    completed: false,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                  };
                  delete newTaskData.id;

                  await firebaseDatabaseService.addTask(user.uid, newTaskData);
                  existingDates.add(newDateString);
                }
              }
              break;

            case 'weekly': {
              const weeksDiff = Math.floor(daysDiff / 7);
              for (let i = 1; i <= weeksDiff; i++) {
                const newDate = new Date(lastDate);
                newDate.setDate(newDate.getDate() + (i * 7));
                const newDateString = newDate.toISOString().split('T')[0];

                if (existingDates.has(newDateString)) continue;

                if (newDate <= today) {
                  const newTaskData = {
                    ...task,
                    date: newDate,
                    dateString: newDateString,
                    completed: false,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                  };
                  delete newTaskData.id;

                  await firebaseDatabaseService.addTask(user.uid, newTaskData);
                  existingDates.add(newDateString);
                }
              }
              break;
            }

            case 'monthly': {
              const newDate = new Date(lastDate);
              newDate.setMonth(newDate.getMonth() + 1);
              const newDateString = newDate.toISOString().split('T')[0];

              if (!existingDates.has(newDateString) && newDate <= today) {
                const newTaskData = {
                  ...task,
                  date: newDate,
                  dateString: newDateString,
                  completed: false,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                };
                delete newTaskData.id;

                await firebaseDatabaseService.addTask(user.uid, newTaskData);
                existingDates.add(newDateString);
              }
              break;
            }

            case 'weekdays':
              for (let i = 1; i <= daysDiff; i++) {
                const newDate = new Date(lastDate);
                newDate.setDate(newDate.getDate() + i);
                const dayOfWeek = newDate.getDay();
                const newDateString = newDate.toISOString().split('T')[0];

                if (dayOfWeek >= 1 && dayOfWeek <= 5 && newDate <= today) {
                  if (existingDates.has(newDateString)) continue;

                  const newTaskData = {
                    ...task,
                    date: newDate,
                    dateString: newDateString,
                    completed: false,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                  };
                  delete newTaskData.id;

                  await firebaseDatabaseService.addTask(user.uid, newTaskData);
                  existingDates.add(newDateString);
                }
              }
              break;
          }
        }
      }
    } catch (error) {
      console.error('Error extending recurring tasks:', error);
    }
  }, [user]);

  // Load tasks from Firebase when user or date changes
  useEffect(() => {
    if (!user) {
      setTasks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let dateFilter = null;
      if (selectedDate) {
        if (selectedDate instanceof Date) {
          const utcDate = new Date(selectedDate.getTime() - (selectedDate.getTimezoneOffset() * 60000));
          dateFilter = utcDate.toISOString().split('T')[0];
        } else {
          dateFilter = selectedDate?.toISOString().split('T')[0];
        }
      }

      const unsubscribe = firebaseDatabaseService.onTasksChanged(
        user.uid,
        (firebaseTasks) => {
          const processedTasks = processTasksWithGaps(firebaseTasks);
          setTasks(processedTasks);
          setLoading(false);
        },
        dateFilter
      );

      return () => {
        unsubscribe();
      };
    } catch (err) {
      console.error('Failed to load tasks:', err);
      setError(err.message || 'Failed to load tasks');
      setTasks([]);
      setLoading(false);
    }
  }, [user, selectedDate]);

  // Auto-extend recurring tasks periodically (check weekly)
  useEffect(() => {
    if (!user) return;
    extendRecurringTasks();
    const interval = setInterval(extendRecurringTasks, 7 * 24 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, extendRecurringTasks]);

  // Process tasks to insert gaps between them
  const processTasksWithGaps = (firebaseTasks) => {
    // Sort tasks by start time
    const sortedTasks = [...firebaseTasks].sort((a, b) => {
      const timeA = a.startTime ? a.startTime.split(':').map(Number) : [0, 0];
      const timeB = b.startTime ? b.startTime.split(':').map(Number) : [0, 0];
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    });

    // Detect overlapping tasks
    const overlapIds = new Set();
    for (let i = 0; i < sortedTasks.length; i++) {
      for (let j = i + 1; j < sortedTasks.length; j++) {
        const a = sortedTasks[i];
        const b = sortedTasks[j];
        if (!a.startTime || !b.startTime || a.allDay || b.allDay) continue;
        const aStart = parseTime(a.startTime), aEnd = parseTime(a.endTime);
        const bStart = parseTime(b.startTime), bEnd = parseTime(b.endTime);
        if (aStart < bEnd && bStart < aEnd) {
          overlapIds.add(a.id);
          overlapIds.add(b.id);
        }
      }
    }

    // Insert gaps between tasks
    const tasksWithGaps = [];
    for (let i = 0; i < sortedTasks.length; i++) {
      const taskWithKind = {
        ...sortedTasks[i],
        kind: sortedTasks[i].kind || 'task',
        hasOverlap: overlapIds.has(sortedTasks[i].id),
      };
      tasksWithGaps.push(taskWithKind);

      if (i < sortedTasks.length - 1) {
        const currentTask = taskWithKind;
        const nextTask = sortedTasks[i + 1];
        const gap = calculateGap(currentTask.endTime, nextTask.startTime);

        // Only show gap prompt for positive gaps of 30 minutes or more
        if (gap >= 30) {
          tasksWithGaps.push({
            id: `gap-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            kind: 'gap',
            label: 'Break',
            durationText: formatGapDuration(gap),
            gapMinutes: gap,
            onClick: onAddTask ? () => {
              onAddTask({
                gapDuration: gap,
                suggestedStartTime: currentTask.endTime,
                suggestedEndTime: nextTask.startTime,
                date: selectedDate,
              });
            } : undefined,
          });
        }
      }
    }

    return tasksWithGaps;
  };

  // Generate recurring task instances
  function generateRecurringTasks(taskData) {
    const { repeat, date } = taskData;
    const tasks = [];

    if (!repeat || repeat === 'none' || typeof repeat !== 'object') {
      return [taskData];
    }

    const startDate = new Date(date);
    const currentDate = new Date(startDate);

    let endDate = new Date(currentDate);

    if (repeat.endDate) {
      endDate = new Date(repeat.endDate);
    } else {
      endDate.setDate(endDate.getDate() + 90);
    }

    while (currentDate <= endDate) {
      if (shouldCreateTaskForDate(currentDate, repeat, startDate)) {
        const taskInstance = {
          ...taskData,
          date: new Date(currentDate),
          dateString: currentDate.toISOString().split('T')[0],
          createdAt: new Date(),
          updatedAt: new Date(),
          isRecurring: true,
          recurringPattern: repeat,
          seriesEndDate: repeat.endDate || null,
        };

        tasks.push(taskInstance);
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return tasks;
  }

  // Check if a task should be created for a specific date based on repeat pattern
  function shouldCreateTaskForDate(currentDate, repeatConfig, originalDate) {
    const { frequency, interval = 1, byWeekday = [] } = repeatConfig;

    switch (frequency) {
      case 'daily': {
        const daysDiff = Math.floor((currentDate - originalDate) / (1000 * 60 * 60 * 24));
        return daysDiff % interval === 0 && daysDiff >= 0;
      }

      case 'weekly': {
        const weeksDiff = Math.floor((currentDate - originalDate) / (1000 * 60 * 60 * 24 * 7));
        const currentDayOfWeek = currentDate.getDay();

        if (weeksDiff % interval !== 0 || weeksDiff < 0) {
          return false;
        }

        if (byWeekday.length === 0) {
          return currentDayOfWeek === originalDate.getDay();
        }

        return byWeekday.includes(currentDayOfWeek);
      }

      case 'monthly': {
        const monthsDiff = (currentDate.getFullYear() - originalDate.getFullYear()) * 12 +
                           (currentDate.getMonth() - originalDate.getMonth());

        if (monthsDiff % interval !== 0 || monthsDiff < 0) {
          return false;
        }

        return currentDate.getDate() === originalDate.getDate();
      }

      default:
        return false;
    }
  }

  const addTask = useCallback(async (newTaskData) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      if (newTaskData.repeat && newTaskData.repeat !== 'none' && typeof newTaskData.repeat === 'object') {
        const recurringTasks = generateRecurringTasks(newTaskData);

        const createdTasks = [];
        for (const taskData of recurringTasks) {
          const result = await firebaseDatabaseService.addTask(user.uid, taskData);
          if (result.success) {
            createdTasks.push(result.task);
          }
        }

        return createdTasks[0];
      } else {
        const taskData = {
          kind: 'task',
          title: newTaskData.title,
          startTime: newTaskData.startTime,
          endTime: newTaskData.endTime,
          durationText: calculateDuration(newTaskData.startTime, newTaskData.endTime),
          color: newTaskData.color,
          completed: false,
          date: newTaskData.date || new Date(),
          allDay: newTaskData.allDay || false,
          repeat: newTaskData.repeat || 'none',
          hasSubtask: newTaskData.hasSubtask || false,
          notes: newTaskData.notes || '',
          subtasks: newTaskData.subtasks || [],
        };

        const result = await firebaseDatabaseService.addTask(user.uid, taskData);

        if (!result.success) {
          throw new Error(result.error?.message || 'Failed to add task');
        }

        return result.task;
      }
    } catch (error) {
      console.error('Failed to add task:', error);
      throw error;
    }
  }, [user]);

  // Helper function to determine which fields actually changed
  const getChangedFields = (originalTask, updates, isRecurringTask = false) => {
    const changedFields = {};
    const protectedFieldsForRecurring = ['date', 'dateString', 'createdAt'];

    Object.keys(updates).forEach(key => {
      const originalValue = originalTask[key];
      const newValue = updates[key];

      if (isRecurringTask && protectedFieldsForRecurring.includes(key)) {
        return;
      }

      if (key === 'date' && originalValue && newValue) {
        const originalDate = new Date(originalValue).toISOString().split('T')[0];
        const newDate = new Date(newValue).toISOString().split('T')[0];
        if (originalDate !== newDate) {
          changedFields[key] = newValue;
        }
      } else if (Array.isArray(originalValue) && Array.isArray(newValue)) {
        if (JSON.stringify(originalValue) !== JSON.stringify(newValue)) {
          changedFields[key] = newValue;
        }
      } else if (typeof originalValue === 'object' && typeof newValue === 'object' &&
               originalValue !== null && newValue !== null) {
        if (JSON.stringify(originalValue) !== JSON.stringify(newValue)) {
          changedFields[key] = newValue;
        }
      } else {
        if (originalValue !== newValue) {
          changedFields[key] = newValue;
        }
      }
    });

    return changedFields;
  };

  // Helper function to update recurring tasks based on scope
  const updateRecurringTask = async (userId, taskId, updates, updateScope, allTasks) => {
    const currentTask = allTasks.find(task => task.id === taskId);
    const currentTaskDate = new Date(currentTask.dateString);

    const recurringTasks = allTasks.filter(task => {
      if (task.id === taskId) return true;

      const sameTitle = task.title === currentTask.title;
      const bothRecurring = task.repeat && currentTask.repeat &&
                           task.repeat !== 'none' && currentTask.repeat !== 'none';

      if (!bothRecurring) return false;

      const taskRepeat = typeof task.repeat === 'object' ? task.repeat : { frequency: task.repeat };
      const currentRepeat = typeof currentTask.repeat === 'object' ? currentTask.repeat : { frequency: currentTask.repeat };

      const sameFrequency = taskRepeat.frequency === currentRepeat.frequency;
      const sameInterval = (taskRepeat.interval || 1) === (currentRepeat.interval || 1);

      return sameTitle && sameFrequency && sameInterval;
    });

    let tasksToUpdate = [];

    switch (updateScope) {
      case 'future':
        tasksToUpdate = recurringTasks.filter(task =>
          new Date(task.dateString) >= currentTaskDate
        );
        break;
      case 'all':
        tasksToUpdate = recurringTasks;
        break;
      default:
        tasksToUpdate = [currentTask];
        break;
    }

    const updatePromises = tasksToUpdate.map(async (task) => {
      const changedFields = getChangedFields(task, updates, true);

      if (changedFields.startTime && changedFields.endTime) {
        changedFields.durationText = calculateDuration(changedFields.startTime, changedFields.endTime);
      } else if (changedFields.startTime && !changedFields.endTime && task.endTime) {
        changedFields.durationText = calculateDuration(changedFields.startTime, task.endTime);
      } else if (!changedFields.startTime && changedFields.endTime && task.startTime) {
        changedFields.durationText = calculateDuration(task.startTime, changedFields.endTime);
      }

      changedFields.updatedAt = new Date();

      const result = await firebaseDatabaseService.updateTask(userId, task.id, changedFields);
      if (!result.success) {
        throw new Error(`Failed to update task ${task.id}: ${result.error?.message}`);
      }
      return result.task;
    });

    const results = await Promise.all(updatePromises);
    return results[0];
  };

  const updateTask = useCallback(async (taskId, updates, updateScope = 'instance') => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const allTasks = await firebaseDatabaseService.getAllTasks(user.uid);
      const currentTask = allTasks.find(task => task.id === taskId);

      if (!currentTask) {
        throw new Error('Task not found');
      }

      const isRecurring = currentTask.repeat && currentTask.repeat !== 'none';

      if (isRecurring && updateScope === 'instance') {
        setRecurringUpdateDialog({
          open: true,
          taskId,
          updates,
        });
        return;
      }

      if (updateScope === 'instance') {
        const changedFields = getChangedFields(currentTask, updates, isRecurring);

        if (changedFields.startTime && changedFields.endTime) {
          changedFields.durationText = calculateDuration(changedFields.startTime, changedFields.endTime);
        } else if (changedFields.startTime && !changedFields.endTime && currentTask.endTime) {
          changedFields.durationText = calculateDuration(changedFields.startTime, currentTask.endTime);
        } else if (!changedFields.startTime && changedFields.endTime && currentTask.startTime) {
          changedFields.durationText = calculateDuration(currentTask.startTime, changedFields.endTime);
        }

        changedFields.updatedAt = new Date();

        const result = await firebaseDatabaseService.updateTask(user.uid, taskId, changedFields);

        if (!result.success) {
          throw new Error(result.error?.message || 'Failed to update task');
        }

        return result.task;
      } else {
        return await updateRecurringTask(user.uid, taskId, updates, updateScope, allTasks);
      }
    } catch (error) {
      console.error('Failed to update task:', error);
      throw error;
    }
  }, [user]);

  // Helper function to delete recurring tasks based on scope
  const deleteRecurringTask = async (userId, taskId, deleteScope, allTasks) => {
    const currentTask = allTasks.find(task => task.id === taskId);
    const currentTaskDate = new Date(currentTask.dateString);

    const recurringTasks = allTasks.filter(task => {
      if (task.id === taskId) return true;

      const sameTitle = task.title === currentTask.title;
      const bothRecurring = task.repeat && currentTask.repeat &&
                           task.repeat !== 'none' && currentTask.repeat !== 'none';

      if (!bothRecurring) return false;

      const taskRepeat = typeof task.repeat === 'object' ? task.repeat : { frequency: task.repeat };
      const currentRepeat = typeof currentTask.repeat === 'object' ? currentTask.repeat : { frequency: currentTask.repeat };

      const sameFrequency = taskRepeat.frequency === currentRepeat.frequency;
      const sameInterval = (taskRepeat.interval || 1) === (currentRepeat.interval || 1);

      return sameTitle && sameFrequency && sameInterval;
    });

    let tasksToDelete = [];

    switch (deleteScope) {
      case 'future':
        tasksToDelete = recurringTasks.filter(task =>
          new Date(task.dateString) >= currentTaskDate
        );
        break;
      case 'all':
        tasksToDelete = recurringTasks;
        break;
      default:
        tasksToDelete = [currentTask];
        break;
    }

    const deletePromises = tasksToDelete.map(async (task) => {
      const result = await firebaseDatabaseService.deleteTask(userId, task.id);
      if (!result.success) {
        throw new Error(`Failed to delete task ${task.id}: ${result.error?.message}`);
      }
      return result;
    });

    const results = await Promise.all(deletePromises);
    return results[0];
  };

  const deleteTask = useCallback(async (taskId, deleteScope = 'instance') => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const allTasks = await firebaseDatabaseService.getAllTasks(user.uid);
      const currentTask = allTasks.find(task => task.id === taskId);

      if (!currentTask) {
        throw new Error('Task not found');
      }

      const isRecurring = currentTask.repeat && currentTask.repeat !== 'none';

      if (isRecurring && deleteScope === 'instance') {
        setRecurringDeleteDialog({
          open: true,
          taskId,
        });
        return;
      }

      if (deleteScope === 'instance') {
        const result = await firebaseDatabaseService.deleteTask(user.uid, taskId);

        if (!result.success) {
          throw new Error(result.error?.message || 'Failed to delete task');
        }

        return result;
      } else {
        return await deleteRecurringTask(user.uid, taskId, deleteScope, allTasks);
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      throw error;
    }
  }, [user]);

  const handleRecurringUpdateChoice = useCallback(async (choice) => {
    if (!recurringUpdateDialog.taskId) return;

    try {
      const allTasks = await firebaseDatabaseService.getAllTasks(user.uid);
      await updateRecurringTask(user.uid, recurringUpdateDialog.taskId, recurringUpdateDialog.updates, choice, allTasks);
      closeRecurringUpdateDialog();
    } catch (error) {
      console.error('Failed to update recurring task:', error);
      throw error;
    }
  }, [user, recurringUpdateDialog]);

  const closeRecurringUpdateDialog = useCallback(() => {
    setRecurringUpdateDialog({
      open: false,
      taskId: null,
      updates: null,
    });
  }, []);

  const handleRecurringDeleteChoice = useCallback(async (choice) => {
    if (!recurringDeleteDialog.taskId) return;

    try {
      const allTasks = await firebaseDatabaseService.getAllTasks(user.uid);
      await deleteRecurringTask(user.uid, recurringDeleteDialog.taskId, choice, allTasks);
      closeRecurringDeleteDialog();
    } catch (error) {
      console.error('Failed to delete recurring task:', error);
      throw error;
    }
  }, [user, recurringDeleteDialog]);

  const closeRecurringDeleteDialog = useCallback(() => {
    setRecurringDeleteDialog({
      open: false,
      taskId: null,
    });
  }, []);

  const toggleTaskComplete = useCallback(async (taskId, completed) => {
    try {
      await updateTask(taskId, { completed });
    } catch (error) {
      console.error('Failed to toggle task completion:', error);
      throw error;
    }
  }, [updateTask]);

  return {
    tasks,
    loading,
    error,
    addTask,
    deleteTask,
    updateTask,
    toggleTaskComplete,
    extendRecurringTasks,
    recurringUpdateDialog,
    handleRecurringUpdateChoice,
    closeRecurringUpdateDialog,
    recurringDeleteDialog,
    handleRecurringDeleteChoice,
    closeRecurringDeleteDialog,
  };
}

// Helper functions
function calculateDuration(startTime, endTime) {
  const start = parseTime(startTime);
  const end = parseTime(endTime);
  const diff = end - start;
  const hours = Math.floor(diff / 60);
  const minutes = diff % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${minutes}m`;
  }
}

function parseTime(timeStr) {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

function calculateGap(endTime, startTime) {
  // Returns positive value when nextStart > prevEnd (real gap)
  // Returns negative/zero when tasks overlap or are back-to-back
  return parseTime(startTime) - parseTime(endTime);
}

function formatGapDuration(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0 && mins > 0) {
    return `${hours}h ${mins}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${mins}m`;
  }
}
